import { isDemoMode } from "@/lib/runtimeMode";
import { useState, type ReactNode } from "react";
import { useNotify } from "ra-core";
import type { AttachmentNote } from "../types";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { getStoredFilePath } from "../providers/supabase/storagePath";
import { ATTACHMENTS_BUCKET } from "../providers/commons/attachments";
export const attachmentFileName = (title?: string) =>
  (title?.split(/[\\/]/).pop() || "Файл")
    .split("")
    .filter(
      (character) =>
        character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127,
    )
    .join("") || "Файл";
export function DownloadAttachment({
  attachment,
  children,
  className,
}: {
  attachment: AttachmentNote;
  children: ReactNode;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const name = attachmentFileName(attachment.title);
  return (
    <a
      href={attachment.src}
      download={name}
      className={className}
      aria-label={`Скачать ${name}`}
      aria-busy={busy}
      onClick={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
          let url: string;
          let revoke = false;
          const path =
            !isDemoMode() && import.meta.env.VITE_SUPABASE_URL
              ? getStoredFilePath(
                  attachment.src,
                  import.meta.env.VITE_SUPABASE_URL,
                  ATTACHMENTS_BUCKET,
                )
              : undefined;
          if (path) {
            const { data, error } = await getSupabaseClient()
              .storage.from(ATTACHMENTS_BUCKET)
              .createSignedUrl(path, 60, { download: name });
            if (error || !data) throw new Error("Не удалось подготовить файл");
            url = data.signedUrl;
          } else {
            const response = await fetch(attachment.src);
            if (!response.ok) throw new Error("Не удалось загрузить файл");
            url = URL.createObjectURL(await response.blob());
            revoke = true;
          }
          const link = document.createElement("a");
          link.href = url;
          link.download = name;
          link.rel = "noopener";
          document.body.appendChild(link);
          link.click();
          link.remove();
          if (revoke) window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
          notify(
            "Не удалось скачать файл. Обновите карточку и попробуйте ещё раз.",
            { type: "error" },
          );
        } finally {
          setBusy(false);
        }
      }}
    >
      {children}
    </a>
  );
}
